import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Warehouse as WarehouseIcon, Loader2, Truck, PackageCheck, Tag, CheckCircle2, Upload, Ban } from "lucide-react";
import { api, apiError } from "../lib/api";
import { AuthImage } from "../components/AuthImage";
import { useAuth } from "../context/AuthContext";

const STAGE_ORDER = ["PLACED", "PURCHASED", "GUANGZHOU_HUB", "CUSTOMS", "INDO_WAREHOUSE", "DISPATCHED_KEDAI", "READY_PICKUP", "COMPLETED"];

const TXT = {
  id: {
    title_cn: "Portal Gudang Guangzhou", title_jkt: "Portal Gudang Jakarta",
    sub: "Input nomor resi tiap barang untuk menandai barang sudah sampai di gudang.",
    markingIn: "Pengajuan Marking Masuk", noMarking: "Belum ada pengajuan marking.",
    pcs: "pcs", hs: "HS Code", markCode: "Kode marking", whAddr: "Alamat gudang",
    sendCode: "Kirim Kode", arrived: "Barang Sampai", inputResi: "Input Resi Barang",
    noOrders: "Tidak ada barang yang menunggu di tahap ini.", resi: "No. Resi",
    markChina: "Tandai Sampai Gudang China", markIndo: "Tandai Sampai Indonesia",
    statusUpdated: "Status diperbarui", fillCodeAddr: "Isi kode marking & alamat gudang",
    codeSent: "Kode marking dikirim", markedArrived: "Barang ditandai sampai gudang",
    statusLbl: "status", kg: "kg", uploadProof: "Upload foto bukti barang", needProof: "Upload dulu foto bukti barang", proofUploaded: "Foto bukti terunggah",
    editCode: "Ubah Kode", save: "Simpan", cancel: "Batal", reject: "Tolak Barang", rejectPrompt: "Alasan penolakan (mis. dilarang regulasi):", rejected: "Barang ditolak",
    st_PENDING: "MENUNGGU", st_APPROVED: "DISETUJUI", st_RECEIVED: "DITERIMA", st_REJECTED: "DITOLAK",
  },
  zh: {
    title_cn: "\u5e7f\u5dde\u4ed3\u5e93\u95e8\u6237", title_jkt: "\u96c5\u52a0\u8fbe\u4ed3\u5e93\u95e8\u6237",
    sub: "\u5f55\u5165\u6bcf\u4ef6\u8d27\u7269\u7684\u5feb\u9012\u5355\u53f7\uff0c\u6807\u8bb0\u8d27\u7269\u5df2\u5230\u4ed3\u3002",
    markingIn: "\u5f85\u5904\u7406\u5507\u5934\u7533\u8bf7", noMarking: "\u6682\u65e0\u5507\u5934\u7533\u8bf7\u3002",
    pcs: "\u4ef6", hs: "HS\u7f16\u7801", markCode: "\u5507\u5934\u7f16\u7801", whAddr: "\u4ed3\u5e93\u5730\u5740",
    sendCode: "\u53d1\u9001\u7f16\u7801", arrived: "\u8d27\u7269\u5230\u4ed3", inputResi: "\u5f55\u5165\u5feb\u9012\u5355\u53f7",
    noOrders: "\u5f53\u524d\u9636\u6bb5\u6682\u65e0\u5f85\u5904\u7406\u8d27\u7269\u3002", resi: "\u5feb\u9012\u5355\u53f7",
    markChina: "\u6807\u8bb0\u5df2\u5230\u5e7f\u5dde\u4ed3", markIndo: "\u6807\u8bb0\u5df2\u5230\u5370\u5c3c",
    statusUpdated: "\u72b6\u6001\u5df2\u66f4\u65b0", fillCodeAddr: "\u8bf7\u586b\u5199\u5507\u5934\u7f16\u7801\u548c\u4ed3\u5e93\u5730\u5740",
    codeSent: "\u5507\u5934\u7f16\u7801\u5df2\u53d1\u9001", markedArrived: "\u5df2\u6807\u8bb0\u8d27\u7269\u5230\u4ed3",
    statusLbl: "\u72b6\u6001", kg: "\u516c\u65a4", uploadProof: "\u4e0a\u4f20\u5230\u8d27\u7167\u7247", needProof: "\u8bf7\u5148\u4e0a\u4f20\u5230\u8d27\u7167\u7247", proofUploaded: "\u5230\u8d27\u7167\u7247\u5df2\u4e0a\u4f20",
    editCode: "\u4fee\u6539\u5507\u5934", save: "\u4fdd\u5b58", cancel: "\u53d6\u6d88", reject: "\u62d2\u7edd\u8d27\u7269", rejectPrompt: "\u62d2\u7edd\u539f\u56e0\uff08\u5982\u53d7\u6cd5\u89c4\u9650\u5236\uff09\uff1a", rejected: "\u8d27\u7269\u5df2\u62d2\u7edd",
    st_PENDING: "\u5f85\u5904\u7406", st_APPROVED: "\u5df2\u6279\u51c6", st_RECEIVED: "\u5df2\u5230\u4ed3", st_REJECTED: "\u5df2\u62d2\u7edd",
  },
};

export default function Warehouse({ lang = "id" }) {
  const t = TXT[lang] || TXT.id;
  const { user } = useAuth();
  const isChina = user?.role === "gudang_china";
  const target = isChina ? "GUANGZHOU_HUB" : "INDO_WAREHOUSE";
  const targetLabel = isChina ? t.markChina : t.markIndo;
  const [orders, setOrders] = useState([]);
  const [markings, setMarkings] = useState([]);
  const [resi, setResi] = useState({});
  const [appr, setAppr] = useState({});
  const [busy, setBusy] = useState({});
  const [proof, setProof] = useState({});
  const [pUp, setPUp] = useState({});
  const [oProof, setOProof] = useState({});
  const [oUp, setOUp] = useState({});
  const [editing, setEditing] = useState({});

  const load = () => {
    api.get("/orders").then((r) => setOrders(r.data.orders));
    if (isChina) api.get("/markings").then((r) => setMarkings(r.data.markings)).catch(() => {});
  };
  useEffect(load, [isChina]);

  const tidx = STAGE_ORDER.indexOf(target);
  const pending = orders.filter((o) => STAGE_ORDER.indexOf(o.status) < tidx);

  const advance = async (id) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try { await api.patch(`/orders/${id}/status`, { status: target, resi: resi[id] || "", photos: oProof[id] ? [oProof[id]] : [] }); toast.success(t.statusUpdated); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy((b) => ({ ...b, [id]: false })); }
  };
  const onOrderPhoto = async (id, e) => {
    const f = e.target.files[0];
    if (!f) return;
    setOUp((s) => ({ ...s, [id]: true }));
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await api.post("/uploads", fd); setOProof((s) => ({ ...s, [id]: data.path })); toast.success(t.proofUploaded); }
    catch (err) { toast.error(apiError(err)); } finally { setOUp((s) => ({ ...s, [id]: false })); }
  };

  const approveMark = async (id) => {
    const f = appr[id] || {};
    if (!f.marking_code || !f.warehouse_address) return toast.error(t.fillCodeAddr);
    try { await api.patch(`/markings/${id}/approve`, { marking_code: f.marking_code, warehouse_address: f.warehouse_address, warehouse_note: f.warehouse_note || "" }); toast.success(t.codeSent); setEditing((s) => ({ ...s, [id]: false })); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const receiveMark = async (id) => {
    if (!proof[id]) return toast.error(t.needProof);
    try { await api.patch(`/markings/${id}/received`, { proof_photo: proof[id] }); toast.success(t.markedArrived); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const onProofFile = async (id, e) => {
    const f = e.target.files[0];
    if (!f) return;
    setPUp((s) => ({ ...s, [id]: true }));
    const fd = new FormData();
    fd.append("file", f);
    try { const { data } = await api.post("/uploads", fd); setProof((s) => ({ ...s, [id]: data.path })); toast.success(t.proofUploaded); }
    catch (err) { toast.error(apiError(err)); } finally { setPUp((s) => ({ ...s, [id]: false })); }
  };

  const startEdit = (m) => {
    setAppr((s) => ({ ...s, [m.id]: { marking_code: m.marking_code, warehouse_address: m.warehouse_address, warehouse_note: m.warehouse_note || "" } }));
    setEditing((s) => ({ ...s, [m.id]: true }));
  };
  const rejectMark = async (id) => {
    const reason = window.prompt(t.rejectPrompt);
    if (reason === null || !reason.trim()) return;
    try { await api.patch(`/markings/${id}/reject`, { reason }); toast.success(t.rejected); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const stLabel = (s) => t[`st_${s}`] || s;

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      <div className="flex items-center gap-3 mb-2"><WarehouseIcon className="text-sky-600" size={26} /><h1 className="font-display text-2xl font-bold text-stone-900">{isChina ? t.title_cn : t.title_jkt}</h1></div>
      <p className="text-stone-500 mb-6 text-sm">{t.sub}</p>

      {isChina && (
        <section className="mb-10" data-testid="wh-markings">
          <h2 className="font-display font-semibold text-lg mb-3 flex items-center gap-2"><Tag size={18} />{t.markingIn}</h2>
          <div className="space-y-3">
            {markings.length === 0 && <p className="text-stone-400 text-sm">{t.noMarking}</p>}
            {markings.map((m) => {
              const f = appr[m.id] || {};
              const setF = (k, v) => setAppr((s) => ({ ...s, [m.id]: { ...s[m.id], [k]: v } }));
              return (
                <div key={m.id} data-testid={`wh-mark-${m.id}`} className="bg-white rounded-2xl border border-stone-200 p-4">
                  <div className="flex items-start gap-3">
                    {m.photo_path ? <AuthImage path={m.photo_path} className="w-16 h-16 rounded-lg object-cover" /> : <div className="w-16 h-16 rounded-lg bg-stone-100" />}
                    <div className="flex-1">
                      <p className="font-medium text-stone-800 text-sm">{m.product_type} · {m.qty} {t.pcs}</p>
                      {m.hscode && <p className="text-xs text-stone-400">{t.hs}: {m.hscode}</p>}
                      {m.notes && <p className="text-xs text-stone-400">{m.notes}</p>}
                      <span className={`inline-block mt-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${m.status === "RECEIVED" ? "bg-emerald-100 text-emerald-700" : m.status === "APPROVED" ? "bg-sky-100 text-sky-700" : m.status === "REJECTED" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{stLabel(m.status)}</span>
                    </div>
                  </div>
                  {m.status === "PENDING" && (
                    <div className="mt-3 space-y-2">
                      <div className="grid sm:grid-cols-3 gap-2">
                        <input data-testid={`wh-code-${m.id}`} value={f.marking_code || ""} onChange={(e) => setF("marking_code", e.target.value)} placeholder={t.markCode} className="border border-stone-200 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-sky-400" />
                        <input data-testid={`wh-addr-${m.id}`} value={f.warehouse_address || ""} onChange={(e) => setF("warehouse_address", e.target.value)} placeholder={t.whAddr} className="border border-stone-200 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-sky-400" />
                        <button data-testid={`wh-approve-${m.id}`} onClick={() => approveMark(m.id)} className="bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-3 py-1.5 rounded-lg flex items-center justify-center gap-1.5"><CheckCircle2 size={15} />{t.sendCode}</button>
                      </div>
                      <button data-testid={`wh-reject-${m.id}`} onClick={() => rejectMark(m.id)} className="text-red-600 text-xs font-semibold flex items-center gap-1 hover:text-red-700"><Ban size={13} />{t.reject}</button>
                    </div>
                  )}
                  {m.status === "APPROVED" && (
                    <div className="mt-3 bg-sky-50 border border-sky-100 rounded-lg p-2.5 text-xs space-y-2">
                      {editing[m.id] ? (
                        <div className="grid sm:grid-cols-3 gap-2">
                          <input data-testid={`wh-edit-code-${m.id}`} value={f.marking_code || ""} onChange={(e) => setF("marking_code", e.target.value)} placeholder={t.markCode} className="border border-stone-200 rounded-lg px-2 py-1.5 outline-none focus:border-sky-400" />
                          <input data-testid={`wh-edit-addr-${m.id}`} value={f.warehouse_address || ""} onChange={(e) => setF("warehouse_address", e.target.value)} placeholder={t.whAddr} className="border border-stone-200 rounded-lg px-2 py-1.5 outline-none focus:border-sky-400" />
                          <div className="flex gap-2">
                            <button data-testid={`wh-save-${m.id}`} onClick={() => approveMark(m.id)} className="flex-1 bg-sky-600 hover:bg-sky-700 text-white font-semibold px-3 py-1.5 rounded-lg">{t.save}</button>
                            <button onClick={() => setEditing((s) => ({ ...s, [m.id]: false }))} className="px-3 py-1.5 rounded-lg border border-stone-200">{t.cancel}</button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between gap-2">
                          <span>{t.markCode} <b className="font-mono">{m.marking_code}</b> · {m.warehouse_address}</span>
                          <button data-testid={`wh-edit-${m.id}`} onClick={() => startEdit(m)} className="text-sky-700 font-semibold whitespace-nowrap">{t.editCode}</button>
                        </div>
                      )}
                      <div className="flex flex-wrap items-center gap-2">
                        <label className="flex items-center gap-1.5 border border-dashed border-sky-300 rounded-lg px-3 py-1.5 cursor-pointer hover:border-sky-500 bg-white">
                          {pUp[m.id] ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />}{proof[m.id] ? t.proofUploaded : t.uploadProof}
                          <input data-testid={`wh-proof-${m.id}`} type="file" accept="image/*" onChange={(e) => onProofFile(m.id, e)} className="hidden" />
                        </label>
                        {proof[m.id] && <AuthImage path={proof[m.id]} className="w-10 h-10 rounded object-cover" />}
                        <button data-testid={`wh-received-${m.id}`} onClick={() => receiveMark(m.id)} disabled={!proof[m.id]} className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1.5 disabled:opacity-50"><PackageCheck size={14} />{t.arrived}</button>
                      </div>
                    </div>
                  )}
                  {m.status === "RECEIVED" && m.proof_photo && (
                    <div className="mt-2"><AuthImage path={m.proof_photo} className="w-20 h-20 rounded-lg object-cover" data-testid={`wh-proof-img-${m.id}`} /></div>
                  )}
                  {m.status === "REJECTED" && (
                    <div className="mt-2 bg-red-50 border border-red-100 rounded-lg p-2.5 text-xs text-red-700" data-testid={`wh-reject-reason-${m.id}`}>{t.rejected}{m.reject_reason ? `: ${m.reject_reason}` : ""}</div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      <h2 className="font-display font-semibold text-lg mb-3 flex items-center gap-2"><Truck size={18} />{t.inputResi}</h2>
      <div className="space-y-3">
        {pending.length === 0 && <p className="text-stone-400 text-sm" data-testid="wh-no-orders">{t.noOrders}</p>}
        {pending.map((o) => (
          <div key={o.id} data-testid={`wh-order-${o.id}`} className="bg-white rounded-2xl border border-stone-200 p-4 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[180px]">
              <p className="font-mono text-xs text-stone-400">{o.awb}</p>
              <p className="text-sm font-medium text-stone-800">{o.items.map((it) => `${it.title} ×${it.qty}`).join(", ")}</p>
              <p className="text-xs text-stone-400">{o.total_weight_kg} {t.kg} · {t.statusLbl}: {o.status}{(isChina ? o.resi_china : o.resi_jakarta) ? ` · ${t.resi}: ${isChina ? o.resi_china : o.resi_jakarta}` : ""}</p>
            </div>
            <input data-testid={`wh-resi-${o.id}`} value={resi[o.id] || ""} onChange={(e) => setResi((s) => ({ ...s, [o.id]: e.target.value }))} placeholder={t.resi} className="border border-stone-200 rounded-lg px-3 py-2 text-sm w-40 outline-none focus:border-sky-400" />
            <label className="flex items-center gap-1.5 border border-dashed border-sky-300 rounded-lg px-3 py-2 cursor-pointer hover:border-sky-500 text-sm bg-white">
              {oUp[o.id] ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />}{oProof[o.id] ? t.proofUploaded : t.uploadProof}
              <input data-testid={`wh-ophoto-${o.id}`} type="file" accept="image/*" onChange={(e) => onOrderPhoto(o.id, e)} className="hidden" />
            </label>
            {oProof[o.id] && <AuthImage path={oProof[o.id]} className="w-10 h-10 rounded object-cover" />}
            <button data-testid={`wh-advance-${o.id}`} onClick={() => advance(o.id)} disabled={busy[o.id]} className="bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-4 py-2 rounded-lg flex items-center gap-1.5 disabled:opacity-60">{busy[o.id] ? <Loader2 className="animate-spin" size={15} /> : <PackageCheck size={15} />}{targetLabel}</button>
          </div>
        ))}
      </div>
    </div>
  );
}
